import { Injectable, signal, WritableSignal } from '@angular/core';
import { Observable, BehaviorSubject, switchMap, catchError, map, throwError, of, first } from 'rxjs';
import { topVendorsDataResponse, UpcomingInterviewsResponse, VendorDetails } from '@interfaces/vendor.model';
import { VendorResponse } from '@interfaces/hiring-manager.model';
import { HiringRequestService } from '@pages/hiring-manager/hiring-request/hiring-request.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ErrorMessages } from '@constants/constants';
import { GlobalErrorService } from '@services/global-error.service';
import { environment } from '@environments/environment';
import { ContractorsVendorView } from '@pages/vendor-rep/contractors-list/contractors-list.config';
import { OpsVendors, ContractorsCount, TimeSheetData, TimesheetReview, TimesheetUpdateResponse, performanceEvaluationData } from '@interfaces/vendor-management-model';
import { TimeSheet } from '@pages/ops-vendor-management/ops-timesheet/ops-timesheet-config';
import { ToasterService } from '@services/toaster.service';
import { TIMESHEET_CONSTANTS } from '@pages/vendor-rep/timesheet/timesheet.constants';
import { ContractorQuarterly } from '@app/shared/constants/constants';
import { QuarterlyFeedbackResponse, OpsQuarterlyFeedbackResponse } from '@app/shared/interfaces/contractor-profile';
@Injectable({
  providedIn: 'root',
})
export class VendorService {
  errorMsg: string = ErrorMessages.NoDataAvailable;
  private baseUrl = environment.apiUrl;
  contractors: WritableSignal<any> = signal(null);
  private vendorDetailsSubject = new BehaviorSubject<VendorDetails[]>([]);
  private currentVendorSubject = new BehaviorSubject<
    VendorDetails | null | undefined
  >(null);
  vendorDetails$ = this.vendorDetailsSubject.asObservable();
  currentVendor$: Observable<VendorDetails | null | undefined> =
    this.currentVendorSubject.asObservable();
  topVendors: WritableSignal<any> = signal(null);

  private contractorsSubject = new BehaviorSubject<ContractorsVendorView[]>([]);
  public contractors$ = this.contractorsSubject.asObservable();


  constructor(
    private hiringRequestService: HiringRequestService,
    private http: HttpClient,
    private errorHandler: GlobalErrorService,
    private toasterService: ToasterService,
  ) { }

  fetchVendorDetails(): Observable<VendorResponse> {
    return this.hiringRequestService.getVendorData();
  }

  setVendorDetails(details: VendorDetails[]): void {
    this.vendorDetailsSubject.next(details);
  }

  setCurrentVendor(vendor: VendorDetails | null | undefined): void {
    this.currentVendorSubject.next(vendor);
  }

  getTopVendors$(month: number, year: number, employeeId: string): Observable<topVendorsDataResponse[]> {
    const params = new HttpParams()
      .set('employeeId', employeeId)
      .set('month', month.toString())
      .set('year', year.toString());

    return this.http
      .get<{ data: topVendorsDataResponse[] }>(`${this.baseUrl}/hiring/top-vendors`, { params })
      .pipe(
        map((resp) => resp?.data ?? []),
        catchError((error) => {
          this.errorHandler.logError(error);
          return throwError(() => error);
        })
      );
  }


  fetchContractorsByVendorId(vendorId: string, month: number, year: number): void {
    const params = new HttpParams()
      .set('vendorId', vendorId)
      .set('month', month.toString())
      .set('year', year.toString());

    const url = `${this.baseUrl}/vendor/contractors-list`;

    this.http
      .get<{ data: ContractorsVendorView[] }>(url, { params })
      .pipe(
        catchError((error) => {
          this.errorHandler.logError(error);
          return of({ data: [] });
        })
      )
      .subscribe({
        next: (response) => {
          this.contractorsSubject.next(response.data);
        },
        error: (error) => {
          this.errorHandler.logComponentError(error);
        },
      });
  }

  getTimesheetDetails(
    month: number,
    year: number,
    vendorId?: string | number | null,
  ): Observable<TimeSheetData[]> {
    if (vendorId != null) {
      const params = new HttpParams()
        .set('month', month.toString())
        .set('year', year.toString())
        .set('vendorId', vendorId.toString());
      return this.http.get<TimeSheetData[]>(
        `${this.baseUrl}/vendor/employee-timesheet`,
        { params },
      ).pipe(
        catchError((error) => this.errorHandler.logError(error)),
      );
    }
    
    return this.currentVendor$.pipe(
      first(),
      switchMap((currentVendor: any) => {
        let params = new HttpParams()
          .set('month', month.toString())
          .set('year', year.toString());
        if (currentVendor?.id) {
          params = params.set('vendorId', currentVendor.id);
        }
        return this.http.get<TimeSheetData[]>(
          `${this.baseUrl}/vendor/employee-timesheet`,
          { params },
        );
      }),
      catchError((error) => this.errorHandler.logError(error)),
    );
  }

  fetchUpcomingInterviews(): Observable<UpcomingInterviewsResponse> {
    return this.currentVendor$.pipe(
      switchMap((currentVendor: any) => {
        let url = `${this.baseUrl}/vendor/upcoming-interviews`;
        if (currentVendor && currentVendor.id) {
          url = `${url}?vendorId=${currentVendor.id}`;
        }
        return this.http.get<UpcomingInterviewsResponse>(url);
      }),
      catchError((error) => {
        return this.errorHandler.logError(error);
      }),
    );
  }

  getCurrentVendor(): Observable<VendorDetails | null | undefined> {
    return this.currentVendor$;
  }

  getCurrentVendorName(): Observable<string | null> {
    return this.getCurrentVendor().pipe(
      map((currentVendor) =>
        currentVendor && typeof currentVendor.name === 'string'
          ? currentVendor.name
          : null,
      ),
    );
  }

  createLeaveDetails(leaveDetails: any): Observable<any> {
    const url = `${this.baseUrl}/vendor/leave-details`;
    return this.http.post(url, leaveDetails).pipe(
      catchError((error) => {
        return this.errorHandler.logError(error);
      }),
    );
  }

  getCbreLeaves(location: string, year: number): Observable<string[]> {
    const params = new HttpParams()
      .set('location', location)
      .set('year', year.toString());
    return this.http.get<any>(`${this.baseUrl}/vendor/cbre-leaves`, { params }).pipe(
      map((response) => {
        if (response.status === 'SUCCESS' && response.data) {
          return (response.data as any[]).map((item: any) => item.date as string);
        }
        return [];
      }),
      catchError(() => of([])),
    );
  }

  getLeaveSummary(
    employeeId: string,
    location: string,
    vendorId: number | null,
    month: number,
    year: number,
  ): Observable<any> {
    return this.currentVendor$.pipe(
      switchMap((currentVendor: any) => {
        let params = new HttpParams()
          .set('location', location)
          .set('month', month.toString())
          .set('year', year.toString())
          .set('vendorId', vendorId || '');
        if (currentVendor && currentVendor.id) {
          params = params
            .set('vendorId', currentVendor.id);
        }
        return this.http.get<any>(
          `${this.baseUrl}/vendor/leave-summary/${employeeId}`,
          { params },
        );
      }),
      switchMap((response) => {
        if (response.status === 'SUCCESS' && response.data) {
          return [response.data];
        } else {
          throw new Error(
            response.reason || ErrorMessages.FailedToFetchLeaveSummary,
          );
        }
      }),
      catchError((error) => {
        return this.errorHandler.logError(error);
      }),
    );
  }

  getOpsTopVendors(
    month: number,
    year: number,
  ): Observable<{ status: string; reason: string; data: any[] }> {
    const url = `${this.baseUrl}/ops/top-vendors`;
    const params = { month: month.toString(), year: year.toString() };
    return this.http.get<{ status: string; reason: string; data: any[] }>(url, {
      params,
    });
  }

  getOpsVendorList(month: string, year: string): Observable<{ status: string; reason: string; data: OpsVendors[] }> {
    const url = `${this.baseUrl}/ops/vendor-list?month=${month}&year=${year}`;
    return this.http.get<{ status: string; reason: string; data: OpsVendors[] }>(url).pipe(
      catchError((error) => {
        this.errorHandler.logError(error);
        throw error;
      })
    );
  }

  getOpsContractorsCount(month: number, year: number): Observable<{ status: string; reason: string; data: ContractorsCount[] }> {
    const url = `${this.baseUrl}/ops/contractors-count`;
    const params = { month: month, year: year };
    return this.http.get<{ status: string; reason: string; data: ContractorsCount[] }>(url, { params }).pipe(
      catchError((error) => {
        this.errorHandler.logError(error);
        throw error;
      })
    );
  }

  getOpsTimesheetDetails(month: number, year: number): Observable<TimeSheet[]> {
    let params = new HttpParams()
      .set('month', month.toString())
      .set('year', year.toString());
    return this.http
      .get<TimeSheet[]>(`${this.baseUrl}/ops/employee-timesheet`, { params })
      .pipe(
        catchError((error) => {
          return this.errorHandler.logError(error);
        }),
      );
  }

  reSendMail(body: any): Observable<any> {
    const url = `${this.baseUrl}/ops/employee-timesheet/notification`;
    return this.http.post<any>(url, body).pipe(catchError((error) => {
          return this.errorHandler.logError(error);
        }),
    );
  }

  getOpsContractorsList(month: number, year: number): Observable<any> {
    const params = new HttpParams()
      .set('month', month.toString())
      .set('year', year.toString());

    const url = `${this.baseUrl}/ops/contractors-list`;

    return this.http.get<any>(url, { params }).pipe(
      catchError((error) => {
        this.errorHandler.logError(error);
        throw error;
      })
    );
  }

  getEmployeeDetails(employeeId: string): Observable<any> {
    const params = new HttpParams().set('employeeId', employeeId);

    return this.http.get<any>(`${this.baseUrl}/hiring/employee`, { params }).pipe(
      catchError((error) => {
        alert(`Error loading employee details: ${error.message || error.status || 'Unknown error'}`);
        throw error;
      })
    );
  }

  /**
   * PUT /hiring/timesheet endpoint with TimesheetReview format
   */
  updateTimesheetStatusForVendorRep(timesheetData: TimesheetReview): Observable<TimesheetUpdateResponse> {
    const url = `${this.baseUrl}/hiring/timesheet`;
    return this.http.put<TimesheetUpdateResponse>(url, timesheetData).pipe(
      catchError((error) => {
        this.toasterService.showError(TIMESHEET_CONSTANTS.MESSAGES.FAILED_UPDATE_TIMESHEET_STATUS, TIMESHEET_CONSTANTS.TITLES.ERROR);
        return throwError(() => error);
      })
    );
  }

  getQuarterlyFeedback(
    employeeId: string,
    year: string,
    quarter: string,
  ): Observable<QuarterlyFeedbackResponse> {
    return this.http
      .get<QuarterlyFeedbackResponse>(
        `${this.baseUrl}/hiring/contractor-feedback?employeeId=${employeeId}&year=${year}&quarter=${quarter}`,
      )
      .pipe(
        catchError((error) => {
          this.toasterService.showError(
            ContractorQuarterly.failedToLoadFeedback,
            ContractorQuarterly.loadError,
          );
          return throwError(() => error);
        }),
      );
  }


  /** Quarter tokens (Q1..Q4) the feedback UI is currently allowed to show.
   *  Backed by is_visible on vendor_management.contractor_feedback_quarter;
   *  toggled via DB update, not code deploy. Callers should intersect their
   *  4-quarter window with this list. */
  getVisibleFeedbackQuarters(): Observable<string[]> {
    return this.http
      .get<{ data: string[] }>(
        `${this.baseUrl}/hiring/contractor-feedback/visible-quarters`,
      )
      .pipe(
        map((res) => res?.data ?? []),
        catchError(() => of<string[]>([])),
      );
  }

  getOpsQuarterlyFeedback(
    quarter: string,
    year: string,
    employeeId?: string,
  ): Observable<OpsQuarterlyFeedbackResponse> {
    let params = new HttpParams()
      .set('quarter', quarter)
      .set('year', year);
    if (employeeId) {
      params = params.set('employeeId', employeeId);
    }
    return this.http.get<OpsQuarterlyFeedbackResponse>(
      `${this.baseUrl}/ops/quarterly-feedback`, { params }
    ).pipe(
      catchError((error) => {
        this.toasterService.showError(
          ContractorQuarterly.failedToLoadFeedback,
          ContractorQuarterly.loadError,
        );
        return throwError(() => error);
      }),
    );
  }

  getPerformanceOnQuartersDetails(
    quarter: string,
    year: number,
  ): Observable<performanceEvaluationData[]> {
    return this.currentVendor$.pipe(
      switchMap((currentVendor: any) => {
        let params = new HttpParams();
        if (currentVendor && currentVendor.id) {
          params = params
            .set('vendorId', currentVendor.id)
            .set('year', year.toString())
            .set('quarter', quarter);
        }

        return this.http.get<performanceEvaluationData[]>(
          `${this.baseUrl}/vendor/vendor-contractors-feedback`,
          {
            params,
          },
        );
      }),
      catchError((error) => {
        return this.errorHandler.logError(error);
      }),
    );
  }

  excludeEmployeeFromTimesheets(employeeId: string, month: number, year: number): Observable<any> {
    const url = `${this.baseUrl}/hiring/inactive/${employeeId}?isIncluded=true&month=${month}&year=${year}`;
    return this.http.put<any>(url, {}).pipe(
      catchError((error) => this.errorHandler.logError(error))
    );
  }

  deleteOpsTimesheet(employeeId: string, month: number, year: number): Observable<any> {
    const url = `${this.baseUrl}/ops/employee-timesheet?employeeId=${employeeId}&month=${month}&year=${year}`;
    return this.http.delete<any>(url).pipe(
      catchError((error) => this.errorHandler.logError(error))
    );
  }

  recalculateEmployeeTimesheet(
    employeeId: string,
    billingDates: string[],
    month?: number,
    year?: number,
  ): Observable<any> {
    const body: any = { employeeId, billingDates };
    if (billingDates.length === 0) {
      body.month = month;
      body.year = year;
    }
    return this.http.post<any>(`${this.baseUrl}/vendor/employee-timesheet/recalculated`, body).pipe(
      catchError((error) => this.errorHandler.logError(error))
    );
  }

updateLastWorkingDay(payload: {
  employeeId: string;
  lastWorkingDay: string | null;
  city: string;
  updatedBy: string;
}): Observable<any> {
  const url = `${this.baseUrl}/ops/employee/lwd`;
  return this.http.post<any>(url, payload).pipe(
    catchError((error) => this.errorHandler.logError(error))
  );
}
}